import type { DefaultTheme, RuleSet } from 'styled-components';
import type { ThemeMode } from './themeMode';

declare module 'styled-components' {
  interface DefaultTheme {
    mode?: ThemeMode;
  }
}

/** Semantic colors of the dark (`_blck`) onboarding design */
export const darkPalette = {
  background: '#212121',
  surface: '#2f2f2f',
  surfaceAccent: '#623718',
  surfaceElevated: '#323232',
  border: '#464646',
  divider: '#2f2f2f',
  textPrimary: '#ffffff',
  textSecondary: '#e3e3e3',
  textTertiary: '#acacac',
  textMuted: '#919191',
  iconSubtle: '#5e5e5e',
  accent: '#429df0',
  accentHover: '#2f8ae0',
  accentDisabled: '#315f88',
  accentStrong: '#1f7ace',
  onAccent: '#000000',
  onAccentDisabled: 'rgba(0, 0, 0, 0.6)',
  accentSurface: '#182a39',
  accentBorder: '#13497c',
  accentMuted: '#315f88',
  highlight: '#71e0ef',
  danger: '#f43f5e',
  dangerSurface: '#f5516d',
  warningSurface: '#422006',
  warningBorder: '#854d0e',
  overlay: 'rgba(0, 0, 0, 0.7)',
} as const;

/** Overrides of `theme.colors` used by `@applyft-web/ui-components` */
export const darkThemeColors: Record<string, string> = {
  text: darkPalette.textPrimary,
  bodyBackground: darkPalette.background,
  primary: darkPalette.accent,
  buttonHover: darkPalette.accentHover,
  buttonColor: darkPalette.onAccent,
  buttonDisabled: darkPalette.accentDisabled,
  buttonDisabledTextColor: darkPalette.onAccentDisabled,
  inputBg: darkPalette.surface,
  circularProgressBg: darkPalette.surface,
  reviewItemBg: darkPalette.surface,
  optionInactive: darkPalette.surface,
  optionBorderInactive: darkPalette.surface,
  optionActive: darkPalette.accentSurface,
  optionBorderActive: darkPalette.accent,
  optionActiveText: darkPalette.textPrimary,
  optionImgActive: darkPalette.accentMuted,
  optionImgInactive: darkPalette.surfaceElevated,
  optionCheckActive: darkPalette.accent,
  optionCheckInactive: darkPalette.border,
  threeDots: darkPalette.accent,
  progressBarActive: darkPalette.accent,
  progressBarBg: darkPalette.surface,
  planItemBorder: darkPalette.border,
  planItemBg: darkPalette.surface,
  planItemTextColorInactive: darkPalette.textTertiary,
  planItemCheckBorder: darkPalette.accent,
  planItemCheckBg: darkPalette.surface,
  planItemLabelBg: darkPalette.accentSurface,
  planItemLabelColorActive: darkPalette.onAccent,
  planItemLabelColorInactive: darkPalette.textPrimary,
};

export const isDarkTheme = (theme?: DefaultTheme) => theme?.mode === 'dark';

/**
 * Applies styles only in dark theme, light styles stay untouched:
 *
 * ```ts
 * const Title = styled.div`
 *   color: #232323;
 *   ${darkMode(css`
 *     color: ${darkPalette.textPrimary};
 *   `)}
 * `;
 * ```
 */
export const darkMode =
  (styles: RuleSet<object>) =>
  ({ theme }: { theme: DefaultTheme }) =>
    isDarkTheme(theme) && styles;
